import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Space,
  message,
  Tabs,
  Row,
  Col,
  Typography,
  Divider,
  Popconfirm,
  Spin
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined,
  BankOutlined,
  FileTextOutlined,
  DollarOutlined
} from '@ant-design/icons';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { Option } = Select;

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
}

interface AccountingSettingsData {
  id: string;
  companyId: string;
  baseCurrencyId: string | null;
  defaultReceivableAccountId: string | null;
  defaultPayableAccountId: string | null;
  defaultBankAccountId: string | null;
  defaultRevenueAccountId: string | null;
  defaultExpenseAccountId: string | null;
  defaultInventoryAccountId: string | null;
  defaultCOGSAccountId: string | null;
  invoicePrefix: string;
  invoiceNextNumber: number;
  invoiceDefaultDueDays: number;
  invoiceDefaultPaymentTerms: string | null;
  invoiceDefaultTermsConditions: string | null;
  invoiceFooterNotes: string | null;
  quotationDefaultPaymentTerms: string | null;
  quotationDefaultTermsConditions: string | null;
  quotationFooterNotes: string | null;
  billPrefix: string;
  billNextNumber: number;
  billDefaultDueDays: number;
  paymentPrefix: string;
  paymentNextNumber: number;
  fiscalYearStartMonth: number;
  fiscalYearStartDay: number;
  autoPostInvoices: boolean;
  autoPostBills: boolean;
  requireApprovalForPayments: boolean;
  lockPeriodOnClose: boolean;
  baseCurrency?: Currency | null;
}

const AccountingSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AccountingSettingsData | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, accountsRes, currenciesRes] = await Promise.all([
        apiService.get('/accounting/settings'),
        apiService.get('/accounting/accounts'),
        apiService.get('/accounting/currencies')
      ]);

      const settingsData = settingsRes.data;
      setSettings(settingsData);
      setAccounts(accountsRes.data || []);
      setCurrencies(currenciesRes.data || []);

      // Set form values
      form.setFieldsValue({
        ...settingsData,
        baseCurrencyId: settingsData?.baseCurrencyId || undefined
      });
    } catch (error: any) {
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();

      await apiService.put('/accounting/settings', values);
      message.success('Settings saved successfully');
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await apiService.post('/accounting/settings/reset');
      message.success('Settings reset to defaults');
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to reset settings');
    }
  };

  const filterAccounts = (type: string) => {
    return accounts.filter(a => a.accountType === type);
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Form form={form} layout="vertical">
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>Accounting Settings</span>
          </Space>
        }
        extra={
          <Space>
            <Popconfirm
              title="Reset all settings to defaults?"
              description="This will clear all customized settings."
              onConfirm={handleReset}
            >
              <Button icon={<ReloadOutlined />}>Reset to Defaults</Button>
            </Popconfirm>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
              Save Settings
            </Button>
          </Space>
        }
      >
        <Tabs
          defaultActiveKey="general"
          items={[
            {
              key: 'general',
              label: (
                <span>
                  <SettingOutlined />
                  General
                </span>
              ),
              children: (
                <>
                  <Title level={5}>Base Currency</Title>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="baseCurrencyId" label="Base Currency">
                        <Select allowClear placeholder="Select base currency">
                          {currencies.map(c => (
                            <Option key={c.id} value={c.id}>
                              {c.symbol} {c.code} - {c.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Title level={5}>Fiscal Year Settings</Title>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="fiscalYearStartMonth" label="Fiscal Year Start Month">
                        <Select>
                          {months.map(m => (
                            <Option key={m.value} value={m.value}>{m.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="fiscalYearStartDay" label="Start Day">
                        <InputNumber min={1} max={31} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Title level={5}>Automation Settings</Title>
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item name="autoPostInvoices" valuePropName="checked" label="Auto-post Invoices">
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="autoPostBills" valuePropName="checked" label="Auto-post Bills">
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="requireApprovalForPayments" valuePropName="checked" label="Require Payment Approval">
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="lockPeriodOnClose" valuePropName="checked" label="Lock Period on Close">
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )
            },
            {
              key: 'accounts',
              label: (
                <span>
                  <BankOutlined />
                  Default Accounts
                </span>
              ),
              children: (
                <>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Configure default accounts for automatic journal entries
                  </Text>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="defaultReceivableAccountId" label="Accounts Receivable (AR)">
                        <Select allowClear placeholder="Select AR account" showSearch optionFilterProp="children">
                          {filterAccounts('ASSET').map(a => (
                            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="defaultPayableAccountId" label="Accounts Payable (AP)">
                        <Select allowClear placeholder="Select AP account" showSearch optionFilterProp="children">
                          {filterAccounts('LIABILITY').map(a => (
                            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="defaultBankAccountId" label="Bank Account">
                        <Select allowClear placeholder="Select bank account" showSearch optionFilterProp="children">
                          {filterAccounts('ASSET').map(a => (
                            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="defaultRevenueAccountId" label="Revenue Account">
                        <Select allowClear placeholder="Select revenue account" showSearch optionFilterProp="children">
                          {filterAccounts('REVENUE').map(a => (
                            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="defaultExpenseAccountId" label="Expense Account">
                        <Select allowClear placeholder="Select expense account" showSearch optionFilterProp="children">
                          {filterAccounts('EXPENSE').map(a => (
                            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="defaultInventoryAccountId" label="Inventory Account">
                        <Select allowClear placeholder="Select inventory account" showSearch optionFilterProp="children">
                          {filterAccounts('ASSET').map(a => (
                            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="defaultCOGSAccountId" label="Cost of Goods Sold (COGS)">
                        <Select allowClear placeholder="Select COGS account" showSearch optionFilterProp="children">
                          {filterAccounts('EXPENSE').map(a => (
                            <Option key={a.id} value={a.id}>{a.code} - {a.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )
            },
            {
              key: 'invoices',
              label: (
                <span>
                  <FileTextOutlined />
                  Invoices
                </span>
              ),
              children: (
                <>
                  <Title level={5}>Invoice Numbering</Title>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="invoicePrefix" label="Invoice Prefix">
                        <Input placeholder="e.g., INV-" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="invoiceNextNumber" label="Next Invoice Number">
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="invoiceDefaultDueDays" label="Default Due Days">
                        <InputNumber min={0} max={365} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Title level={5}>Default Content</Title>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="invoiceDefaultPaymentTerms" label="Default Payment Terms">
                        <Input.TextArea
                          rows={3}
                          placeholder="e.g., Net 30 days, Payment due upon receipt, 50% upfront 50% on delivery, etc."
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="invoiceDefaultTermsConditions" label="Default Terms & Conditions">
                        <Input.TextArea
                          rows={4}
                          placeholder="e.g., Goods sold are not refundable, All disputes subject to Hong Kong law, Late payments subject to 1.5% monthly interest, etc."
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="invoiceFooterNotes" label="Default Footer Notes">
                        <Input.TextArea rows={2} placeholder="e.g., Thank you for your business!" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )
            },
            {
              key: 'quotations',
              label: (
                <span>
                  <FileTextOutlined />
                  Quotations
                </span>
              ),
              children: (
                <>
                  <Title level={5}>Default Content</Title>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="quotationDefaultPaymentTerms" label="Default Payment Terms">
                        <Input.TextArea
                          rows={3}
                          placeholder="e.g., Net 30 days, Payment due upon receipt, etc."
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="quotationDefaultTermsConditions" label="Default Terms & Conditions">
                        <Input.TextArea
                          rows={4}
                          placeholder="e.g., Quote valid for 30 days, prices subject to change without notice, etc."
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="quotationFooterNotes" label="Default Notes">
                        <Input.TextArea rows={2} placeholder="e.g., Thank you for considering us!" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )
            },
            {
              key: 'bills',
              label: (
                <span>
                  <DollarOutlined />
                  Bills & Payments
                </span>
              ),
              children: (
                <>
                  <Title level={5}>Bill Numbering</Title>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="billPrefix" label="Bill Prefix">
                        <Input placeholder="e.g., BILL-" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="billNextNumber" label="Next Bill Number">
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="billDefaultDueDays" label="Default Due Days">
                        <InputNumber min={0} max={365} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Title level={5}>Payment Numbering</Title>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="paymentPrefix" label="Payment Prefix">
                        <Input placeholder="e.g., PAY-" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="paymentNextNumber" label="Next Payment Number">
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )
            }
          ]}
        />
      </Card>
    </Form>
  );
};

export default AccountingSettings;
